import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { LicenseStatus, LicenseType } from '../../../types/manageLicenses';
import type { ManageLicenseListDto } from '../../../types/manageLicenses';

// ── Inline helpers ────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: LicenseStatus }) {
  const { t } = useTranslation();
  const label = {
    [LicenseStatus.Active]:    t('App.Common.LicenseStatus.Active'),
    [LicenseStatus.Suspended]: t('App.Common.LicenseStatus.Suspended'),
    [LicenseStatus.Revoked]:   t('App.Common.LicenseStatus.Revoked'),
    [LicenseStatus.Expired]:   t('App.Common.LicenseStatus.Expired'),
  }[status] ?? LicenseStatus[status];
  const colour = {
    [LicenseStatus.Active]:    'bg-green-100 text-green-800',
    [LicenseStatus.Suspended]: 'bg-amber-100 text-amber-800',
    [LicenseStatus.Revoked]:   'bg-red-100 text-red-800',
    [LicenseStatus.Expired]:   'bg-neutral-100 text-neutral-700',
  }[status] ?? 'bg-neutral-100 text-neutral-700';
  return <span className={`px-2 py-0.5 rounded text-xs ${colour}`}>{label}</span>;
}

function expirationClass(iso?: string): string {
  if (!iso) return 'text-neutral-500';
  const date = new Date(iso);
  const now = new Date();
  if (date < now) return 'text-red-600 font-semibold';
  const days = (date.getTime() - now.getTime()) / 86400000;
  if (days <= 30) return 'text-amber-600';
  return '';
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

// ── RowActionsMenu ────────────────────────────────────────────────────────────

interface RowActionsMenuProps {
  row: ManageLicenseListDto;
  permissions: {
    canRevoke: boolean;
    canSuspend: boolean;
    canManage: boolean;
  };
  onOpen: () => void;
  onRequestAction: (action: 'revoke' | 'suspend' | 'reactivate' | 'extend') => void;
}

function RowActionsMenu({ row, permissions, onOpen, onRequestAction }: RowActionsMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="px-2 py-1 text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 rounded"
        aria-label={t('App.Admin.Licenses.LicenseListRow.RowActionsAriaLabel')}
      >
        ⋯
      </button>

      {open && (
        <div className="absolute right-0 mt-1 z-10 bg-white border border-neutral-200 rounded shadow-md py-1 w-40">
          <button
            type="button"
            onClick={() => { setOpen(false); onOpen(); }}
            className="block w-full text-left px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            {t('App.Admin.Licenses.LicenseListRow.OpenDetails')}
          </button>

          {permissions.canManage && (
            <button
              type="button"
              onClick={() => { setOpen(false); onRequestAction('extend'); }}
              className="block w-full text-left px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50"
            >
              {t('App.Admin.Licenses.LicenseListRow.Extend')}
            </button>
          )}

          {permissions.canRevoke && (
            <button
              type="button"
              disabled={row.status === LicenseStatus.Revoked}
              onClick={() => { setOpen(false); onRequestAction('revoke'); }}
              className="block w-full text-left px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('App.Admin.Licenses.LicenseListRow.Revoke')}
            </button>
          )}

          {permissions.canSuspend && (
            <button
              type="button"
              disabled={row.status === LicenseStatus.Suspended}
              onClick={() => { setOpen(false); onRequestAction('suspend'); }}
              className="block w-full text-left px-3 py-1.5 text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('App.Admin.Licenses.LicenseListRow.Suspend')}
            </button>
          )}

          {permissions.canManage && (
            <button
              type="button"
              disabled={row.status !== LicenseStatus.Suspended}
              onClick={() => { setOpen(false); onRequestAction('reactivate'); }}
              className="block w-full text-left px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('App.Admin.Licenses.LicenseListRow.Reactivate')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── LicenseListRow ────────────────────────────────────────────────────────────

interface Props {
  row: ManageLicenseListDto;
  selected: boolean;
  onToggleSelect: () => void;
  onOpen: () => void;
  hidden: (col: string) => boolean;
  permissions: {
    canRevoke: boolean;
    canSuspend: boolean;
    canManage: boolean;
  };
  onRequestAction: (action: 'revoke' | 'suspend' | 'reactivate' | 'extend') => void;
}

export default function LicenseListRow({
  row, selected, onToggleSelect, onOpen, hidden, permissions, onRequestAction,
}: Props) {
  const { t } = useTranslation();
  const devicesOverLimit = row.activeDeviceCount >= row.maxConcurrentDevices;

  return (
    <tr className={`border-t border-neutral-100 ${row.isDeleted ? 'opacity-50 bg-neutral-50' : 'hover:bg-neutral-50'}`}>
      {/* 1. Checkbox */}
      <td className="p-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={t('App.Admin.Licenses.LicenseListRow.SelectAriaLabel', { email: row.studentEmail })}
        />
      </td>

      {/* 2. Student email */}
      <td className="p-2">
        <button type="button" onClick={onOpen} className="text-primary-600 hover:underline">
          {row.studentEmail}
        </button>
        {row.isDeleted && <span className="ml-2 text-xs uppercase text-red-600">{t('App.Admin.Licenses.LicenseListRow.DeletedBadge')}</span>}
      </td>

      {/* Optional: studentName */}
      {!hidden('studentName') && (
        <td className="p-2">{row.studentName || <span className="text-neutral-400">—</span>}</td>
      )}

      {/* 3. Book title */}
      <td className="p-2">{row.bookTitle}</td>

      {/* Optional: publisher */}
      {!hidden('publisher') && (
        <td className="p-2">{row.publisherName ?? '—'}</td>
      )}

      {/* 4. Type */}
      <td className="p-2">{t(`App.Common.LicenseType.${LicenseType[row.licenseType]}`)}</td>

      {/* 5. Status */}
      <td className="p-2">
        <StatusChip status={row.status} />
      </td>

      {/* Optional: activation */}
      {!hidden('activation') && (
        <td className="p-2">{formatDate(row.activationDate)}</td>
      )}

      {/* 6. Expiration */}
      <td className="p-2">
        {row.expirationDate
          ? <span className={expirationClass(row.expirationDate)}>{formatDate(row.expirationDate)}</span>
          : <em className="text-neutral-500">{t('App.Admin.Licenses.LicenseListRow.Perpetual')}</em>}
      </td>

      {/* 7. Devices */}
      <td className={`p-2 tabular-nums ${devicesOverLimit ? 'font-bold text-red-600' : ''}`}>
        {row.activeDeviceCount} / {row.maxConcurrentDevices}
      </td>

      {/* Optional: lastActive */}
      {!hidden('lastActive') && (
        <td className="p-2">—</td>
      )}

      {/* Optional: externalLicenseId */}
      {!hidden('externalLicenseId') && (
        <td className="p-2 font-mono text-xs">{row.externalLicenseId ?? '—'}</td>
      )}

      {/* Optional: bookseller */}
      {!hidden('bookseller') && (
        <td className="p-2">{row.booksellerName ?? '—'}</td>
      )}

      {/* 8. Actions */}
      <td className="p-2 text-right">
        <RowActionsMenu
          row={row}
          permissions={permissions}
          onOpen={onOpen}
          onRequestAction={onRequestAction}
        />
      </td>
    </tr>
  );
}
